import { compact, groupBy, isEmpty } from 'lodash';
import type { IDaypart } from 'src/interfaces/Daypart';
import type { ILocationGroup } from 'src/interfaces/LocationGroup';
import type { IScheduleDaypart } from 'src/interfaces/ScheduleDaypart';
import type { IScheduleDaypartOverride } from 'src/interfaces/ScheduleDaypartOverride';

export interface DaypartWithWeekdays extends IDaypart {
  scheduleDayparts: IScheduleDaypart[];
  enabled: boolean;
}

export interface StructuredDaypartData extends Partial<ILocationGroup> {
  dayparts: DaypartWithWeekdays[];
}

export const mapDaypartsWithOverrides = (
  daypartsData: IScheduleDaypart[],
  overridesData: IScheduleDaypartOverride[],
): StructuredDaypartData => {
  const locationGroup = daypartsData[0]?.locationGroup;

  const overridesMap = new Map(
    overridesData.map((override) => [override.schedule_daypart_id, override]),
  );

  const groupedDayparts = groupBy(
    daypartsData,
    (daypart) => daypart.weekdayDaypart?.daypart_id,
  );

  const dayparts = compact(
    Object.entries(groupedDayparts).map(([_daypartId, daypartRecords]) => {
      const daypart = daypartRecords[0]?.weekdayDaypart?.daypart;

      if (!daypart) {
        return null;
      }

      const scheduleDaypartsWithOverrides = daypartRecords.map((record) => ({
        ...record,
        override: overridesMap.get(record.id),
      }));

      const enabled = scheduleDaypartsWithOverrides.some((item) => {
        const startTime =
          item.override?.start_time !== undefined
            ? item.override.start_time
            : item.start_time;

        return !isEmpty(startTime);
      });

      return {
        ...daypart,
        scheduleDayparts: scheduleDaypartsWithOverrides,
        enabled,
      } as DaypartWithWeekdays;
    }),
  );

  return {
    ...locationGroup,
    dayparts,
  };
};
